import { useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';
import { useCategories } from '@/api/categories';
import { NotificationBell } from '@/components/panel/NotificationBell';
import { CookieBanner, cookieNoticePending } from '@/components/layout/CookieBanner';
import { WhatsAppButton } from '@/components/layout/WhatsAppButton';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { cn } from '@/lib/cn';
import { BrandMark } from '@/components/layout/BrandMark';
import { t } from '@/i18n';

/** Vitrin sayfalarının ortak çerçevesi (docs/05 §2). */
export function PublicLayout() {
  // Çerez bandı açıkken WhatsApp düğmesi yukarı kalkar; bant kapanınca iner.
  const [noticeOpen, setNoticeOpen] = useState(cookieNoticePending);

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      {/*
        `min-h`: veri gelene kadar sayfa kısa kalıyor ve alt bilgi yukarıda duruyordu;
        içerik yüklenince aşağı kayıp düzen kaymasına (CLS) yol açıyordu.
      */}
      <main className="min-h-[70vh] flex-1">
        <Outlet />
      </main>
      <SiteFooter />
      <WhatsAppButton raised={noticeOpen} />
      <CookieBanner onDismiss={() => setNoticeOpen(false)} />
    </div>
  );
}

function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-surface/95 backdrop-blur">
      <div className="container-page flex h-16 items-center justify-between gap-4">
        <div className="flex items-center gap-6">
          <Link to="/" className="shrink-0 rounded-lg" aria-label={t('app.name')}>
            <BrandMark />
          </Link>

          <nav aria-label={t('nav.categories')} className="hidden items-center gap-5 lg:flex">
            <CategoryMenu />
            {/*
              İlanlar, Alım Talepleri ve Nasıl Çalışır menüden kaldırıldı (müşteri).
              Alım talebi ürünün tamamından çıktı; ilanlara ana sayfadaki vitrinden
              giriliyor. Menüde yalnız kategori gezinmesi kalıyor.
            */}
          </nav>
        </div>

        <HeaderSearch />

        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageSwitcher />
          <HeaderActions />
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-label={t('nav.categories')}
            className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden"
          >
            <span aria-hidden="true" className="block text-lg leading-none">
              ☰
            </span>
          </button>
        </div>
      </div>

      {menuOpen && <MobileMenu onNavigate={() => setMenuOpen(false)} />}
    </header>
  );
}

/** Hover ile açılan kategori menüsü (docs/05 §2). */
function CategoryMenu() {
  const categories = useCategories();

  return (
    <div className="group relative">
      <button
        type="button"
        className="flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-primary-900"
      >
        {t('nav.categories')}
        <span aria-hidden="true" className="text-xs">
          ▾
        </span>
      </button>

      <div className="invisible absolute left-0 top-full z-50 w-[44rem] pt-3 opacity-0 transition group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
        <div className="grid grid-cols-3 gap-x-6 gap-y-4 rounded-card border border-slate-200 bg-surface p-5 shadow-lg">
          {categories.data?.map((parent) => (
            <div key={parent.id}>
              <Link
                to={`/kategori/${parent.slug}`}
                className="text-sm font-semibold text-slate-900 hover:text-primary-900"
              >
                {parent.name}
              </Link>
              <ul className="mt-1.5 space-y-1">
                {parent.children.map((child) => (
                  <li key={child.id}>
                    <Link
                      to={`/kategori/${child.slug}`}
                      className="text-xs text-slate-600 hover:text-primary-900"
                    >
                      {child.name}
                      <span className="ml-1 text-slate-400">({child.listingCount})</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function HeaderSearch() {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');

  return (
    <form
      className="hidden min-w-0 flex-1 md:block"
      onSubmit={(event) => {
        event.preventDefault();
        const trimmed = term.trim();
        navigate(trimmed ? `/ilanlar?q=${encodeURIComponent(trimmed)}` : '/ilanlar');
      }}
    >
      <input
        type="search"
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        placeholder={t('nav.searchPlaceholder')}
        aria-label={t('common.search')}
        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400"
      />
    </form>
  );
}

/** Oturum durumuna göre giriş/üye ol veya panel bağlantıları. */
function HeaderActions() {
  const user = useAuthStore((state) => state.user);

  if (user) {
    return (
      <div className="flex items-center gap-2">
        <NotificationBell />
        <Link
          to="/panel"
          className="hidden rounded-xl px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 sm:block"
        >
          {t('nav.panel')}
        </Link>
        <Link
          to="/panel/ilan-ver"
          className="rounded-xl bg-primary-800 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-900 sm:px-4"
        >
          {t('nav.createListing')}
        </Link>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Link
        to="/giris"
        className="hidden rounded-xl px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 sm:block"
      >
        {t('nav.login')}
      </Link>
      <Link
        to="/uye-ol"
        className="rounded-xl bg-primary-800 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-900 sm:px-4"
      >
        {t('nav.register')}
      </Link>
    </div>
  );
}

function MobileMenu({ onNavigate }: { onNavigate: () => void }) {
  const categories = useCategories();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [term, setTerm] = useState('');

  return (
    <div className="border-t border-slate-200 bg-surface lg:hidden">
      <div className="container-page space-y-4 py-4">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const trimmed = term.trim();
            navigate(trimmed ? `/ilanlar?q=${encodeURIComponent(trimmed)}` : '/ilanlar');
            onNavigate();
          }}
        >
          <input
            type="search"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder={t('nav.searchPlaceholder')}
            aria-label={t('common.search')}
            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm"
          />
        </form>

        <nav className="space-y-1">
          {[
            // Girişli kullanıcıya "Giriş yap" göstermek kopuk bir uç bırakıyordu:
            // mobilde panele geçecek hiçbir bağlantı yoktu.
            user
              ? { to: '/panel', label: t('nav.panel') }
              : { to: '/giris', label: t('nav.login') },
          ].map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              className="block rounded-lg px-2 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div>
          <p className="px-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
            {t('nav.categories')}
          </p>
          <ul className="mt-1 space-y-1">
            {categories.data?.map((parent) => (
              <li key={parent.id}>
                <Link
                  to={`/kategori/${parent.slug}`}
                  onClick={onNavigate}
                  className="block rounded-lg px-2 py-1.5 text-sm text-slate-600 hover:bg-slate-100"
                >
                  {parent.name}
                  <span className="ml-1 text-slate-400">({parent.listingCount})</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function SiteFooter() {
  const categories = useCategories();

  const columns: { title: string; links: { to: string; label: string }[] }[] = [
    {
      title: t('footer.marketplace'),
      links: (categories.data ?? []).slice(0, 5).map((category) => ({
        to: `/kategori/${category.slug}`,
        label: category.name,
      })),
    },
    {
      title: t('footer.corporate'),
      links: [
        { to: '/sayfa/hakkimizda', label: t('nav.about') },
        { to: '/blog', label: t('nav.blog') },
        { to: '/iletisim', label: t('nav.contact') },
      ],
    },
    {
      title: t('footer.legal'),
      links: [
        { to: '/sayfa/kvkk-aydinlatma-metni', label: t('footer.privacy') },
        { to: '/sayfa/kullanim-kosullari', label: t('footer.terms') },
        { to: '/sayfa/mesafeli-satis-sozlesmesi', label: t('footer.distanceSalesContract') },
      ],
    },
  ];

  return (
    <footer className="border-t border-slate-200 bg-surface">
      <div className="container-page grid gap-8 py-10 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <BrandMark />
          <p className="mt-2 text-sm text-slate-600">{t('app.tagline')}</p>
        </div>

        {columns.map((column) => (
          <nav key={column.title} aria-label={column.title}>
            <p className="text-sm font-semibold text-slate-900">{column.title}</p>
            <ul className="mt-2 space-y-1.5">
              {column.links.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className={cn('text-sm text-slate-600 hover:text-primary-900')}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        ))}
      </div>

      <div className="border-t border-slate-200">
        <div className="container-page flex flex-col items-center justify-between gap-4 py-4 text-sm text-slate-500 sm:flex-row">
          <div>{t('footer.copyright')}</div>
          <LanguageSwitcher />
        </div>
      </div>
    </footer>
  );
}
